import React from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { contextService, type Context } from '@/services/contextService';
import { useAuth } from '@/contexts/AuthContext';
import { triggerFeedbackNudge } from '@/lib/feedbackNudge';
import toast from 'react-hot-toast';

export type { Context };

export function useContexts() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const queryKey = ['contexts', user?.id];

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: loading, error } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam = 0 }) => contextService.fetchAll(pageParam as number, 50),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => lastPage.length === 50 ? allPages.length * 50 : undefined,
        enabled: !!user,
    });

    const contexts = React.useMemo(() => data ? data.pages.flat() : [], [data]);

    const addMutation = useMutation({
        mutationFn: contextService.create,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            toast.success('Contexto criado com sucesso!');
            triggerFeedbackNudge();
        },
        onError: () => {
            toast.error('Erro ao criar contexto.');
        }
    });

    const updateMutation = useMutation({
        mutationFn: contextService.update,
        onMutate: async (updatedContext) => {
            await queryClient.cancelQueries({ queryKey });
            const previousContexts = queryClient.getQueryData<Context[]>(queryKey);
            
            // Optimistically update
            queryClient.setQueryData(queryKey, (old: InfiniteData<Context[]> | undefined) => {
                if (!old) return previousContexts;
                return {
                    ...old,
                    pages: old.pages.map((page: Context[]) =>
                        page.map(c => c.id === updatedContext.id ? { ...c, ...updatedContext } : c)
                    )
                };
            });

            return { previousContexts };
        },
        onError: (_err, _newVal, context) => {
            if (context?.previousContexts) {
                queryClient.setQueryData(queryKey, context.previousContexts);
            }
            toast.error('Erro ao atualizar contexto.');
        },
        onSuccess: () => {
            toast.success('Contexto atualizado com sucesso!');
            triggerFeedbackNudge();
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: contextService.delete,
        onMutate: async (id) => {
            await queryClient.cancelQueries({ queryKey });
            const previousContexts = queryClient.getQueryData<Context[]>(queryKey);
            
            // Optimistically delete
            queryClient.setQueryData(queryKey, (old: InfiniteData<Context[]> | undefined) => {
                if (!old) return previousContexts;
                return {
                    ...old,
                    pages: old.pages.map((page: Context[]) =>
                        page.filter(c => c.id !== id)
                    )
                };
            });

            return { previousContexts };
        },
        onError: (_err, _newVal, context) => {
            if (context?.previousContexts) {
                queryClient.setQueryData(queryKey, context.previousContexts);
            }
            toast.error('Erro ao excluir contexto.');
        },
        onSuccess: () => {
            toast.success('Contexto excluído com sucesso!');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    return {
        contexts,
        loading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        addContext: addMutation.mutateAsync,
        updateContext: updateMutation.mutateAsync,
        deleteContext: deleteMutation.mutateAsync,
    };
}
